import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Mic, Square, Sparkles, Save } from 'lucide-react';

import { Modal } from '../../../components/ui/Modal.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Textarea } from '../../../components/ui/Textarea.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Switch } from '../../../components/ui/Switch.jsx';
import { TagInput } from '../../../components/ui/TagInput.jsx';
import { customersApi } from '../../../api/customers.js';
import { toolsApi } from '../../../api/tools.js';
import { customerDraftsApi } from '../../../api/customerDrafts.js';
import { petsApi } from '../../../api/pets.js';
import { categoriesApi } from '../../../api/categories.js';
import { useStoreOptions } from '../../../hooks/useStoreOptions.js';
import { useDictation } from '../../../hooks/useDictation.js';
import { ApiError } from '../../../api/client.js';
import { applyServerValidation } from '../../../lib/forms.js';
import { t } from '../../../lib/i18n.js';
import { CustomerPetsEditor } from './CustomerPetsEditor.jsx';
import { EMPTY_PET } from './customerPets.constants.js';

// Map an extracted species/breed label to a category id from the loaded
// options, matching by slug then name (case-insensitive). Returns '' when no
// option matches, so the operator picks it manually.
function matchCategory(options, label) {
  if (!label) return '';
  const needle = String(label).trim().toLowerCase();
  const hit = options.find((c) => String(c.slug || '').toLowerCase() === needle)
    || options.find((c) => String(c.name || '').toLowerCase() === needle)
    || options.find((c) => String(c.name || '').toLowerCase().includes(needle));
  return hit ? String(hit._id) : '';
}

function normalizeSex(value) {
  const v = String(value || '').trim().toLowerCase();
  if (['female', 'femmina', 'f'].includes(v)) return 'female';
  if (['male', 'maschio', 'm'].includes(v)) return 'male';
  return 'unknown';
}

const EMPTY = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  storeId: '',
  notes: '',
  tags: [],
  consentData: false,
  consentPhotos: false,
  status: 'active'
};

export function CustomerFormModal({ open, onClose, initial, resumeDraft }) {
  const editing = Boolean(initial && initial._id);
  const queryClient = useQueryClient();

  const { stores: storeOpts } = useStoreOptions({ enabled: open });

  const { register, handleSubmit, reset, control, setError, setValue, getValues, formState: { errors } } = useForm({ defaultValues: EMPTY });

  // Free text draft for taking notes while talking to the customer. Kept local
  // (not part of the customer payload): the operator turns it into fields,
  // manually or with the extraction tool, and reviews before saving. It can be
  // persisted as a CustomerDraft and resumed later.
  const [draft, setDraft] = useState('');
  const [draftId, setDraftId] = useState(null);
  // Animals to create together with the customer. Local state: pets need the
  // customer id, which only exists after the customer is saved.
  const [petList, setPetList] = useState([]);
  const dictBaseRef = useRef('');

  // Species/breed option lists feed both the inline pet editor and the
  // resolution of extracted species/breed labels to ids.
  const speciesQuery = useQuery({ queryKey: ['cat', 'species'], enabled: open && !editing, queryFn: () => categoriesApi.list({ kind: 'species', limit: 100, active: true }) });
  const breedQuery = useQuery({ queryKey: ['cat', 'breed'], enabled: open && !editing, queryFn: () => categoriesApi.list({ kind: 'breed', limit: 100, active: true }) });
  const speciesOpts = useMemo(() => ((speciesQuery.data && speciesQuery.data.items) || []), [speciesQuery.data]);
  const breedOpts = useMemo(() => ((breedQuery.data && breedQuery.data.items) || []), [breedQuery.data]);

  // Reset draft/form state on the open transition (in render, comparing against
  // previous open state, so we never call setState synchronously in an effect).
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setDraft(resumeDraft?.text || '');
      setDraftId(resumeDraft?.id || null);
      setPetList(Array.isArray(resumeDraft?.fields?.pets) ? resumeDraft.fields.pets : []);
    }
  }

  // Which tools are enabled. Drives whether the dictation and extraction
  // actions are shown. Saving a draft does not need any AI tool.
  const toolsQuery = useQuery({
    queryKey: ['tools', 'list'],
    enabled: open,
    queryFn: () => toolsApi.list(),
    staleTime: 60_000
  });
  const toolEnabled = (id) => (toolsQuery.data?.items || []).some((tool) => tool.id === id && tool.enabled);
  const extractEnabled = toolEnabled('customer-extract');
  const dictationEnabled = toolEnabled('dictation');

  const dictation = useDictation({ transcribeFn: toolsApi.transcribe });

  useEffect(() => {
    if (!open) return;
    if (initial) {
      reset({
        firstName: initial.firstName || '',
        lastName: initial.lastName || '',
        email: initial.email || '',
        phone: initial.phone || '',
        storeId: String(initial.storeId?._id || initial.storeId || ''),
        notes: initial.notes || '',
        tags: initial.tags || [],
        consentData: Boolean(initial.consent?.dataProcessing?.granted),
        consentPhotos: Boolean(initial.consent?.photos?.granted),
        status: initial.status || 'active'
      });
    } else if (resumeDraft && resumeDraft.fields) {
      // Rehydrate the form snapshot saved with the draft. Pets live in their own
      // state, so keep them out of the react-hook-form reset.
      const custFields = { ...resumeDraft.fields };
      delete custFields.pets;
      reset({ ...EMPTY, ...custFields, tags: resumeDraft.fields.tags || [] });
    } else {
      reset(EMPTY);
    }
  }, [open, initial, resumeDraft, reset]);

  // Stop any running dictation when the modal closes.
  useEffect(() => {
    if (!open && dictation.recording) dictation.stop();
  }, [open, dictation]);

  const toggleDictation = () => {
    if (dictation.recording) {
      dictation.stop();
      return;
    }
    dictBaseRef.current = draft.trim();
    dictation.start((text) => {
      const base = dictBaseRef.current;
      setDraft(base ? `${base} ${text}` : text);
    });
  };

  const extractMutation = useMutation({
    mutationFn: () => toolsApi.extractCustomer({
      text: draft.trim(),
      storeId: getValues('storeId') || undefined,
      // Offer the same stores shown in the dropdown so the model can resolve a
      // mentioned store name to a real id.
      stores: (storeOpts || []).map((s) => ({ id: String(s._id), name: s.name, code: s.code }))
    }),
    onSuccess: (res) => {
      const d = res?.data || {};
      // Only overwrite a field when the model actually found a value, so the
      // extraction never wipes something the operator typed by hand.
      if (d.firstName != null) setValue('firstName', d.firstName, { shouldDirty: true });
      if (d.lastName != null) setValue('lastName', d.lastName, { shouldDirty: true });
      if (d.email != null) setValue('email', d.email, { shouldDirty: true });
      if (d.phone != null) setValue('phone', d.phone, { shouldDirty: true });
      if (d.storeId != null) setValue('storeId', String(d.storeId), { shouldDirty: true });
      if (d.notes != null) setValue('notes', d.notes, { shouldDirty: true });
      if (Array.isArray(d.tags) && d.tags.length) setValue('tags', d.tags, { shouldDirty: true });
      if (d.consentDataProcessing != null) setValue('consentData', Boolean(d.consentDataProcessing), { shouldDirty: true });
      if (d.consentPhotos != null) setValue('consentPhotos', Boolean(d.consentPhotos), { shouldDirty: true });
      // Resolve extracted animals to editable rows (species/breed text -> id).
      if (Array.isArray(d.pets) && d.pets.length) {
        setPetList(d.pets.map((p) => {
          const speciesId = matchCategory(speciesOpts, p.species);
          const breedScope = breedOpts.filter((b) => String(b.parentId?._id || b.parentId || '') === String(speciesId || ''));
          return {
            ...EMPTY_PET,
            name: p.name || '',
            speciesId,
            breedId: speciesId ? matchCategory(breedScope, p.breed) : '',
            sex: normalizeSex(p.sex),
            weightKg: p.weightKg != null ? String(p.weightKg) : '',
            allergies: Array.isArray(p.allergies) ? p.allergies : [],
            dietaryNotes: p.notes || ''
          };
        }));
      }
      toast.success('Dati estratti. Controlla e correggi prima di salvare.');
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  const saveDraftMutation = useMutation({
    mutationFn: () => {
      const body = { text: draft, fields: { ...getValues(), pets: petList }, storeId: getValues('storeId') || undefined };
      return draftId ? customerDraftsApi.update(draftId, body) : customerDraftsApi.create(body);
    },
    onSuccess: () => {
      toast.success('Bozza salvata. La trovi in "Bozze".');
      queryClient.invalidateQueries({ queryKey: ['customer-drafts'] });
      onClose();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  const mutation = useMutation({
    mutationFn: (values) => {
      const payload = {
        firstName: values.firstName,
        lastName: values.lastName || undefined,
        email: values.email || undefined,
        phone: values.phone || undefined,
        storeId: values.storeId,
        notes: values.notes || undefined,
        tags: values.tags,
        consent: {
          dataProcessing: { granted: Boolean(values.consentData) },
          photos: { granted: Boolean(values.consentPhotos) }
        },
        status: values.status
      };
      for (const k of Object.keys(payload)) if (payload[k] === undefined) delete payload[k];
      return editing ? customersApi.update(initial._id, payload) : customersApi.create(payload);
    },
    onSuccess: async (created) => {
      toast.success(editing ? 'Cliente aggiornato.' : 'Cliente creato.');
      queryClient.invalidateQueries({ queryKey: ['customers'] });

      // Create the inline animals against the new customer (the id only exists
      // now). A failed pet does not roll back the customer; we report a count.
      const customerId = created && created._id;
      if (!editing && customerId) {
        const valid = petList.filter((p) => (p.name || '').trim());
        let failed = 0;
        for (const p of valid) {
          try {
            await petsApi.create({
              customerId,
              name: p.name.trim(),
              speciesId: p.speciesId || null,
              breedId: p.breedId || null,
              sex: p.sex || 'unknown',
              birthDate: p.birthDate || null,
              weightKg: (p.weightKg === '' || p.weightKg == null) ? null : Number(p.weightKg),
              allergies: Array.isArray(p.allergies) ? p.allergies : [],
              dietaryNotes: p.dietaryNotes || undefined
            });
          } catch { failed += 1; }
        }
        if (valid.length) {
          queryClient.invalidateQueries({ queryKey: ['pets'] });
          queryClient.invalidateQueries({ queryKey: ['customer', customerId, 'pets'] });
          if (failed) toast.error(`${failed} animale/i non creati. Aggiungili dalla scheda cliente.`);
          else toast.success(`${valid.length} animale/i aggiunti.`);
        }
      }

      // A draft that became a customer is consumed: remove it.
      if (draftId) {
        try { await customerDraftsApi.remove(draftId); } catch { /* draft already gone */ }
        queryClient.invalidateQueries({ queryKey: ['customer-drafts'] });
      }
      onClose();
    },
    onError: (err) => {
      const applied = applyServerValidation(err, setError);
      if (!applied) toast.error(err instanceof ApiError ? err.message : t('common.error'));
    }
  });

  const busy = mutation.isPending || saveDraftMutation.isPending;
  const showDraftPanel = !editing;

  return (
    <Modal
      open={open}
      onClose={busy ? undefined : onClose}
      title={editing ? `Modifica ${initial?.firstName || 'cliente'}` : (draftId ? 'Riprendi bozza' : 'Nuovo cliente')}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
          {showDraftPanel && (
            <Button variant="secondary" onClick={() => saveDraftMutation.mutate()} loading={saveDraftMutation.isPending} disabled={mutation.isPending}>
              <Save className="h-4 w-4" />
              {draftId ? 'Aggiorna bozza' : 'Salva bozza'}
            </Button>
          )}
          <Button form="customer-form" type="submit" loading={mutation.isPending} disabled={saveDraftMutation.isPending}>{t('common.save')}</Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        {showDraftPanel && (
          <section className="flex flex-col gap-3 rounded-lg border border-dashed border-border bg-surface-subtle p-3">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-sm font-medium text-ink">Bozza appunti</p>
                <p className="text-xs text-ink-muted">Scrivi o detta gli appunti mentre parli. Salva la bozza per riprenderla dopo, oppure estrai i dati nel form.</p>
              </div>
              {dictationEnabled && (
                <Button
                  type="button"
                  variant={dictation.recording ? 'danger' : 'ghost'}
                  size="sm"
                  onClick={toggleDictation}
                  loading={dictation.busy}
                  title={dictation.recording ? 'Ferma dettatura' : 'Detta'}
                >
                  {dictation.recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                  {dictation.recording ? 'Ferma' : 'Detta'}
                </Button>
              )}
            </div>
            <Textarea
              rows={4}
              placeholder="Es. Mario Rossi, cellulare 333 1234567, ha un labrador con allergie, ha dato consenso al trattamento dati..."
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            {dictation.error && <p className="text-xs text-danger">{dictation.error}</p>}
            {extractEnabled && (
              <div className="flex items-center justify-end">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => extractMutation.mutate()}
                  loading={extractMutation.isPending}
                  disabled={!draft.trim()}
                >
                  <Sparkles className="h-4 w-4" />
                  Estrai con AI
                </Button>
              </div>
            )}
          </section>
        )}

        <form id="customer-form" className="flex flex-col gap-5" onSubmit={handleSubmit(mutation.mutate)} noValidate>
          <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input label="Nome" error={errors.firstName?.message} {...register('firstName', { required: 'Nome richiesto' })} />
            <Input label="Cognome" error={errors.lastName?.message} {...register('lastName')} />
            <Input label="Email" type="email" error={errors.email?.message} {...register('email')} />
            <Input label="Telefono" error={errors.phone?.message} {...register('phone')} />
            <Select label="Negozio" error={errors.storeId?.message} {...register('storeId', { required: 'Negozio richiesto' })}>
              <option value="">Seleziona...</option>
              {storeOpts.map((s) => <option key={s._id} value={s._id}>{s.name} ({s.code})</option>)}
            </Select>
            <Select label="Stato" {...register('status')}>
              <option value="active">Attivo</option>
              <option value="archived">Archiviato</option>
            </Select>
          </section>

          <Controller
            control={control}
            name="tags"
            render={({ field }) => <TagInput label="Tag" value={field.value} onChange={field.onChange} placeholder="es. fidelizzato, allergie" lowercase />}
          />

          <Textarea label="Note interne" rows={3} error={errors.notes?.message} {...register('notes')} />

          <section className="flex flex-col gap-3 rounded-lg border border-border bg-surface p-3">
            <p className="text-sm font-medium text-ink">Consensi (raccolti in negozio)</p>
            <Controller
              control={control}
              name="consentData"
              render={({ field }) => (
                <label className="flex items-center justify-between gap-3">
                  <span className="text-sm text-ink-muted">Trattamento dati personali</span>
                  <Switch checked={field.value} onChange={field.onChange} />
                </label>
              )}
            />
            <Controller
              control={control}
              name="consentPhotos"
              render={({ field }) => (
                <label className="flex items-center justify-between gap-3">
                  <span className="text-sm text-ink-muted">Conservazione foto dell'animale</span>
                  <Switch checked={field.value} onChange={field.onChange} />
                </label>
              )}
            />
          </section>

          {!editing && (
            <div className="rounded-lg border border-dashed border-border p-3">
              <CustomerPetsEditor
                value={petList}
                onChange={setPetList}
                speciesOpts={speciesOpts}
                breedOpts={breedOpts}
                disabled={busy}
              />
            </div>
          )}
        </form>
      </div>
    </Modal>
  );
}
